/**
 * OnboardingChecklistCard
 * -----------------------
 * Dashboard card showing the 5-step staff onboarding checklist. Auto-hides
 * once all 5 steps are complete (with a brief "Setup Complete" success
 * confirmation on the run that flips to 5/5).
 *
 * Backed by GET /api/employee/onboarding-progress (computed from existing
 * data — profile, availability, documents — plus two boolean event flags).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, AlertCircle, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { employeeAPI } from '../lib/api';

export default function OnboardingChecklistCard() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await employeeAPI.getOnboardingProgress();
      setState(data);
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh whenever the dashboard regains focus — keeps card in sync after
  // the user has hopped into Profile / Availability / Documents and back.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  if (loading || !state) return null;

  // Auto-hide once everything is done. The "Setup Complete" badge is shown
  // on EmployeeLayout via a separate read of the same endpoint if desired —
  // here we simply hide the card to declutter the dashboard.
  if (state.all_done) return null;

  const pct = Math.round((state.completed / state.total) * 100);

  return (
    <div
      className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white p-5 shadow-sm"
      data-testid="onboarding-checklist-card"
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
            <Sparkles size={18} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Getting Started</h3>
            <p className="text-xs text-slate-500">
              Finish setup so your manager can schedule you confidently.
            </p>
          </div>
        </div>
        <div
          className="text-sm font-semibold text-orange-600 bg-white border border-orange-200 rounded-full px-2.5 py-1"
          data-testid="onboarding-progress-label"
        >
          {state.completed} / {state.total}
        </div>
      </div>

      <div className="mt-3 h-1.5 w-full rounded-full bg-orange-100 overflow-hidden" aria-hidden="true">
        <div
          className="h-full bg-gradient-to-r from-orange-400 to-orange-500 transition-all duration-300"
          style={{ width: `${pct}%` }}
          data-testid="onboarding-progress-bar"
        />
      </div>

      <ol className="mt-4 space-y-2">
        {state.steps.map((step) => (
          <li
            key={step.key}
            className={`flex items-center gap-3 rounded-lg border p-2.5 ${
              step.complete
                ? 'border-green-200 bg-green-50'
                : 'border-slate-200 bg-white'
            }`}
            data-testid={`onboarding-step-${step.key}`}
          >
            <div
              className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center ${
                step.complete
                  ? 'bg-green-500 text-white'
                  : 'bg-orange-100 text-orange-600'
              }`}
            >
              {step.complete ? <Check size={14} /> : <AlertCircle size={14} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${
                step.complete ? 'text-green-800 line-through decoration-green-400/60' : 'text-slate-800'
              }`}>
                {step.label}
              </p>
              <p className="text-xs text-slate-500 truncate">{step.description}</p>
            </div>
            {!step.complete && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2.5 text-xs border-orange-200 text-orange-700 hover:bg-orange-50"
                onClick={() => navigate(step.action_url)}
                data-testid={`onboarding-step-action-${step.key}`}
              >
                {step.action_label}
                <ChevronRight size={12} className="ml-0.5" />
              </Button>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
